/**
 * MomentBlock — the video moment a chat message is about, rendered above the
 * message text: a time pill (clickable only where there's a player to seek),
 * the captured frame's thumbnail (opens the file viewer), and the labels for
 * a frame sent without text or a moment from an earlier cut. The note-card
 * layout's `card` variant shows the frame as a FRAME pill beside the time
 * instead of a thumbnail.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { AnchorFrame, MediaMessageAnchor } from '@discovr/types';

import { CornersOut, Play, Selection, type IconWeight } from '../../../design-system/icons';
import { useInView } from '../../../lib/browser/useInView';
import { cn } from '../../../lib/utils';
import { Skeleton } from '../../ui/skeleton';
import { formatTimecode } from '../../../lib/media/timecode';
import { fetchableFileId, type FileSource } from './fileSource';
import { FrameImage, FrameUnavailable } from './FrameImage';
import { EARLIER_CUT_LABEL, FRAME_ONLY_LABEL, frameAltText } from './moments';

const ZOOM_ICON_PX = 10;
const FRAME_PILL_ICON_PX = 12;
/** The pill names the frame in the design's uppercase micro-label; its accessible name says what it opens. */
const FRAME_PILL_TEXT = 'Frame';

export type MomentBlockVariant = 'bubble' | 'card';
export const MOMENT_BLOCK_BUBBLE: MomentBlockVariant = 'bubble';
export const MOMENT_BLOCK_CARD: MomentBlockVariant = 'card';

const BLOCK_CLASS = 'mb-2 flex flex-col items-start gap-1.5';
const PILL_ROW_CLASS = 'flex items-center gap-2';
/** Staging's read-only stub pill, kept as the base for both the label and the button. */
const PILL_CLASS =
  'inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium tabular-nums text-primary-foreground';
const PILL_BUTTON_CLASS = `${PILL_CLASS} cursor-pointer hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;
/** The note card's outlined pill: card ground, neutral hairline, 24px tall. */
const CARD_PILL_CLASS =
  'inline-flex h-6 items-center gap-1 rounded-full border border-border bg-card px-2.5 text-[12px] font-medium tabular-nums text-foreground';
const CARD_PILL_BUTTON_CLASS = `${CARD_PILL_CLASS} cursor-pointer transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`;
const CARD_FRAME_PILL_CLASS =
  'inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-border bg-card px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const CARD_BLOCK_CLASS = 'flex flex-col items-start gap-1.5';
const CARD_PILL_ROW_CLASS = 'flex flex-wrap items-center gap-1.5';
const EARLIER_CUT_CLASS = 'text-[11px] italic text-muted-foreground';
const THUMB_WIDTH_CLASS = 'w-[160px] max-w-full';
const THUMB_BUTTON_CLASS =
  'group relative block overflow-hidden rounded-md border border-border bg-(--background-player) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const THUMB_IMAGE_CLASS = 'block w-full';
const THUMB_SCRIM_CLASS = 'absolute inset-0 bg-black/0 transition-colors duration-150 group-hover:bg-black/20 motion-reduce:transition-none';
const ZOOM_BADGE_CLASS = 'absolute bottom-[5px] right-[5px] flex rounded-md bg-zinc-950/85 p-[3px] text-white';
/** Holds the thumbnail's place at the frame's own shape; video's shape until the frame says otherwise. */
const THUMB_SKELETON_CLASS = 'block aspect-video rounded-md';
const FRAME_ONLY_CLASS = 'text-[12px] italic text-muted-foreground';

export interface MomentBlockProps {
  anchor: MediaMessageAnchor;
  bodyEmpty: boolean;
  seekable: boolean;
  earlierCut: boolean;
  onSeek?: (tMs: number) => void;
  onOpenFrame: () => void;
  /** The optimistic bubble's own staged frame — its upload isn't done, so
   *  there's no file id to fetch yet. */
  localFrameUrl?: string;
  /** Turns a sent frame's file id into bytes. Absent → a sent frame shows no
   *  thumbnail (surfaces without files). */
  fileSource?: FileSource;
  /** `bubble` (default): the frame as a thumbnail. `card`: outlined pills, the frame as a FRAME pill. */
  variant?: MomentBlockVariant;
}

function seekLabel(tMs: number): string {
  return `Go to ${formatTimecode(tMs)}`;
}

function openFrameLabel(tMs: number): string {
  return `Open frame at ${formatTimecode(tMs)}`;
}

/** How each variant draws the time pill: the bubble's filled stub, or the card's outlined pill. */
interface TimePillStyle {
  labelClass: string;
  buttonClass: string;
  iconPx: number;
  iconWeight: IconWeight;
}

const TIME_PILL_STYLE: Record<MomentBlockVariant, TimePillStyle> = {
  bubble: { labelClass: PILL_CLASS, buttonClass: PILL_BUTTON_CLASS, iconPx: 9, iconWeight: 'fill' },
  card: { labelClass: CARD_PILL_CLASS, buttonClass: CARD_PILL_BUTTON_CLASS, iconPx: 11, iconWeight: 'regular' },
};

interface TimePillProps {
  tMs: number;
  seekable: boolean;
  onSeek?: (tMs: number) => void;
  variant: MomentBlockVariant;
}

function TimePill({ tMs, seekable, onSeek, variant }: TimePillProps) {
  const style = TIME_PILL_STYLE[variant];
  const content = (
    <>
      <Play size={style.iconPx} weight={style.iconWeight} aria-hidden="true" />
      <span>{formatTimecode(tMs)}</span>
    </>
  );

  function handleSeek(): void {
    onSeek?.(tMs);
  }

  if (!seekable) return <span className={style.labelClass}>{content}</span>;
  return (
    <button type="button" onClick={handleSeek} aria-label={seekLabel(tMs)} className={style.buttonClass}>
      {content}
    </button>
  );
}

interface FrameThumbProps {
  tMs: number;
  onOpen: () => void;
  children: ReactNode;
}

/** The thumbnail button — it opens the viewer, where a frame that didn't load can be retried. */
function FrameThumb({ tMs, onOpen, children }: FrameThumbProps) {
  return (
    <button type="button" onClick={onOpen} aria-label={openFrameLabel(tMs)} className={cn(THUMB_BUTTON_CLASS, THUMB_WIDTH_CLASS)}>
      {children}
      <span aria-hidden="true" className={THUMB_SCRIM_CLASS} />
      <span aria-hidden="true" className={ZOOM_BADGE_CLASS}>
        <CornersOut size={ZOOM_ICON_PX} weight="bold" />
      </span>
    </button>
  );
}

interface FrameThumbImageProps {
  src: string;
  tMs: number;
  onOpen: () => void;
}

/** FrameImage also swaps in the placeholder if the bytes won't decode. */
function FrameThumbImage({ src, tMs, onOpen }: FrameThumbImageProps) {
  return (
    <FrameThumb tMs={tMs} onOpen={onOpen}>
      <FrameImage src={src} alt={frameAltText(tMs)} className={THUMB_IMAGE_CLASS} />
    </FrameThumb>
  );
}

/** The frame's own shape, so the page doesn't jump when it arrives; none when it doesn't say. */
function frameShapeStyle(frame: AnchorFrame | undefined): CSSProperties | undefined {
  if (!frame || frame.width <= 0 || frame.height <= 0) return undefined;
  return { aspectRatio: `${frame.width} / ${frame.height}` };
}

interface MomentFrameProps {
  frame?: AnchorFrame;
  localFrameUrl?: string;
  tMs: number;
  onOpen: () => void;
}

/**
 * The one `useFile` call for the frame, held until the thumbnail is on screen;
 * a local frame short-circuits only what's shown. A skeleton holds its place
 * until then, and a frame that fails to load shows as unavailable.
 */
function FetchedMomentFrame({ frame, localFrameUrl, tMs, onOpen, fileSource }: MomentFrameProps & { fileSource: FileSource }) {
  const { ref, inView } = useInView<HTMLDivElement>();
  const fetched = fileSource.useFile(fetchableFileId(frame?.file_id, localFrameUrl), { enabled: inView });
  const src = localFrameUrl ?? fetched.url;
  if (src) return <FrameThumbImage src={src} tMs={tMs} onOpen={onOpen} />;
  if (fetched.isError) {
    return (
      <FrameThumb tMs={tMs} onOpen={onOpen}>
        <FrameUnavailable alt={frameAltText(tMs)} className={THUMB_IMAGE_CLASS} />
      </FrameThumb>
    );
  }
  return <Skeleton ref={ref} aria-hidden="true" className={cn(THUMB_SKELETON_CLASS, THUMB_WIDTH_CLASS)} style={frameShapeStyle(frame)} />;
}

/** A timestamp-only moment has neither. */
function carriesFrame(frameFileId: string | undefined, localFrameUrl: string | undefined): boolean {
  return !!frameFileId || !!localFrameUrl;
}

function MomentFrame({ fileSource, ...moment }: MomentFrameProps & { fileSource?: FileSource }) {
  if (!carriesFrame(moment.frame?.file_id, moment.localFrameUrl)) return null;
  if (fileSource) return <FetchedMomentFrame {...moment} fileSource={fileSource} />;
  if (!moment.localFrameUrl) return null;
  return <FrameThumbImage src={moment.localFrameUrl} tMs={moment.tMs} onOpen={moment.onOpen} />;
}

/** Only a frame sent without text is labelled; a timestamp's files speak for it. */
function isFrameOnly(bodyEmpty: boolean, anchor: MediaMessageAnchor, localFrameUrl: string | undefined): boolean {
  return bodyEmpty && carriesFrame(anchor.frame?.file_id, localFrameUrl);
}

/**
 * Mirrors the bubble's thumbnail: a sent frame needs a file source to open,
 * an optimistic one its local preview; a timestamp-only moment has neither.
 */
function showsFramePill(anchor: MediaMessageAnchor, localFrameUrl: string | undefined, fileSource: FileSource | undefined): boolean {
  if (!carriesFrame(anchor.frame?.file_id, localFrameUrl)) return false;
  return !!fileSource || !!localFrameUrl;
}

interface FramePillProps {
  tMs: number;
  onOpen: () => void;
}

function FramePill({ tMs, onOpen }: FramePillProps) {
  return (
    <button type="button" onClick={onOpen} aria-label={openFrameLabel(tMs)} className={CARD_FRAME_PILL_CLASS}>
      <Selection size={FRAME_PILL_ICON_PX} aria-hidden="true" />
      <span>{FRAME_PILL_TEXT}</span>
    </button>
  );
}

function CardMomentBlock({ anchor, bodyEmpty, seekable, earlierCut, onSeek, onOpenFrame, localFrameUrl, fileSource }: MomentBlockProps) {
  const tMs = anchor.t_ms;

  return (
    <div className={CARD_BLOCK_CLASS} data-testid="moment-block">
      <div className={CARD_PILL_ROW_CLASS}>
        <TimePill tMs={tMs} seekable={seekable} onSeek={onSeek} variant={MOMENT_BLOCK_CARD} />
        {showsFramePill(anchor, localFrameUrl, fileSource) ? <FramePill tMs={tMs} onOpen={onOpenFrame} /> : null}
        {earlierCut ? <span className={EARLIER_CUT_CLASS}>{EARLIER_CUT_LABEL}</span> : null}
      </div>
      {isFrameOnly(bodyEmpty, anchor, localFrameUrl) ? <span className={FRAME_ONLY_CLASS}>{FRAME_ONLY_LABEL}</span> : null}
    </div>
  );
}

export function MomentBlock({ variant = MOMENT_BLOCK_BUBBLE, ...props }: MomentBlockProps) {
  if (variant === MOMENT_BLOCK_CARD) return <CardMomentBlock {...props} />;
  const { anchor, bodyEmpty, seekable, earlierCut, onSeek, onOpenFrame, localFrameUrl, fileSource } = props;
  const tMs = anchor.t_ms;

  return (
    <div className={BLOCK_CLASS} data-testid="moment-block">
      <div className={PILL_ROW_CLASS}>
        <TimePill tMs={tMs} seekable={seekable} onSeek={onSeek} variant={MOMENT_BLOCK_BUBBLE} />
        {earlierCut ? <span className={EARLIER_CUT_CLASS}>{EARLIER_CUT_LABEL}</span> : null}
      </div>
      <MomentFrame frame={anchor.frame} localFrameUrl={localFrameUrl} tMs={tMs} onOpen={onOpenFrame} fileSource={fileSource} />
      {isFrameOnly(bodyEmpty, anchor, localFrameUrl) ? <span className={FRAME_ONLY_CLASS}>{FRAME_ONLY_LABEL}</span> : null}
    </div>
  );
}
